import { ApiError, NetworkError } from '@/api/client';
import type { TranslateFn } from '@/i18n/LanguageContext';
import { describeActionError } from '@/pages/Exceptions/shared';
import type { ScanProductCandidate } from '@/api/types';
import type { ScanBanner } from './ScanBanner';

/** A failure turned into a banner. `retryable`: the request never reached the server, so the SAME opId is sent again. */
export type ErrorBanner = ScanBanner & { readonly retryable: boolean };

/** Owner holding the same barcode more than once at a source cell (SCAN.AMBIGUOUS_OWNER). */
export type OwnerChoice = { readonly ownerId: string; readonly ownerName: string; readonly skuId: string; readonly skuCode: string; readonly skuName: string };

export function ownerChoices(err: unknown): readonly OwnerChoice[] | null {
  if (!(err instanceof ApiError) || err.code !== 'SCAN.AMBIGUOUS_OWNER') return null;
  const raw = (err.params as Record<string, unknown>).candidates;
  try {
    const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return null;
    const out: OwnerChoice[] = [];
    for (const row of parsed) {
      const r = row as Record<string, unknown>;
      if (typeof r.ownerId === 'string' && typeof r.skuId === 'string') {
        out.push({
          ownerId: r.ownerId, skuId: r.skuId, ownerName: String(r.ownerName ?? ''), skuCode: String(r.skuCode ?? ''), skuName: String(r.skuName ?? ''),
        });
      }
    }
    return out.length > 1 ? out : null;
  } catch {
    return null;
  }
}

/** The sentence for any scan failure: plain words, never a code, an id or a request number. */
export function errorBanner(t: TranslateFn, err: unknown): ErrorBanner {
  if (err instanceof NetworkError) return { tone: 'error', title: t('err.network'), retryable: true };
  if (err instanceof ApiError) {
    const p = err.params as Record<string, unknown>;
    if (err.status === 429 || err.code === 'RATE_LIMITED') return { tone: 'warn', title: t('err.rateLimited'), retryable: false };
    switch (err.code) {
      case 'SCAN.NOT_IN_REQUEST':
      case 'SCAN.WRONG_ITEM':
        return { tone: 'error', title: t('scan.err.notInRequest'), retryable: false };
      case 'SCAN.DISCREPANCY_NEEDS_RECEIVING':
        return { tone: 'error', title: t('scan.err.needsReceiving'), retryable: false };
      case 'BARCODE.NOT_FOUND':
        return { tone: 'error', title: t('scan.err.unknown'), retryable: false };
      case 'LOCATION.ARCHIVED':
        return { tone: 'error', title: t('scan.err.archived'), retryable: false };
      case 'STOCK.LOCATION_NOT_ALLOWED':
        if (p.reason === 'OTHER_WAREHOUSE') return { tone: 'error', title: t('scan.err.otherWarehouse'), retryable: false };
        if (p.reason === 'SOURCE') return { tone: 'error', title: t('scan.err.sourceNotAllowed'), retryable: false };
        if (p.reason === 'DESTINATION') return { tone: 'error', title: t('scan.err.destNotAllowed'), retryable: false };
        break;
      case 'PUTAWAY.INVALID_DESTINATION':
        return { tone: 'error', title: t('put.err.destination'), retryable: false };
      case 'STOCK.RESERVED':
        return { tone: 'error', title: t('scan.err.reserved'), retryable: false };
      case 'STOCK.TRACKED_UNSUPPORTED':
        return { tone: 'error', title: t('scan.err.tracked'), retryable: false };
      case 'RECEIVING.SHIPMENT_NOT_IN_RECEIVING':
        return { tone: 'error', title: t('rcv.err.notReceiving'), retryable: false };
      case 'MEMBERSHIP.OUT_OF_SCOPE':
        return { tone: 'error', title: t('scan.err.outOfScope'), retryable: false };
      default:
    }
  }
  return { tone: 'error', title: describeActionError(t, err), retryable: false };
}

/** "T-shirt · M" - what the operator recognises a product by. */
export function productLabel(c: { readonly name: string; readonly size?: string | null | undefined }): string {
  return c.size ? `${c.name} · ${c.size}` : c.name;
}

export function candidateMeta(c: ScanProductCandidate): string {
  return [c.article && c.article !== c.code ? c.article : null, c.size || null, c.color || null].filter(Boolean).join(' · ');
}
